import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useSelector } from 'react-redux';

import { CircularProgress, Theme, WithStyles } from '@material-ui/core';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { createStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Star } from '@material-ui/icons';
import DomainIcon from '@material-ui/icons/Domain';
import { Button, UserAvatar } from 'core';
import { get, map } from 'lodash';
import moment from 'moment';
import { TAvailability, TFacility, TProvider } from 'ui-model';
import { AddressService } from 'ui-utils';

import { EntityID } from '@lib/resources/types';
import { selectCurrentMaster, selectCurrentUser } from '@lib/selectors';

import { SvgIconWrapper } from 'components/v2';

const styles = ({ spacing: { unit }, breakpoints: { down, up }, palette: { divider } }: Theme) =>
  createStyles({
    providerLink: {
      overflow: 'hidden',
    },
    providerText: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: '18px',
    },
    biography: {
      fontSize: 14,
      lineHeight: '18px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    facilityText: {
      flex: 'none',
      paddingLeft: 0,
      marginRight: '30px',
      maxWidth: 'fit-content',
    },
    providers: {
      borderTop: `1px solid ${divider}`,
      padding: unit,
      marginBottom: unit * 2,
    },
    provider: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: unit * 5,
      [down('sm')]: {
        flexDirection: 'column',
        marginLeft: 0,
        marginBottom: unit,
      },
    },
    providerAvatar: {
      [down('sm')]: {
        display: 'none',
      },
    },
    providerName: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: unit * 1.5,
      width: '30%',
      [down('sm')]: {
        marginLeft: 0,
        padding: `0px ${unit}px`,
        width: '100%',
        flexDirection: 'row',
      },
    },
    buttonPCP: {
      display: 'inline-flex',
      pointerEvents: 'none',
      fontSize: 11,
      fontWeight: 600,
      padding: '0 6px',
      borderRadius: 4,
      marginLeft: unit,
      '& svg': {
        fontSize: 11,
        marginRight: unit / 2,
      },
    },
    availabilities: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      padding: '0px 8px',
      width: '70%',
      [down('sm')]: {
        width: '100%',
        padding: 0,
      },
    },
    button: {
      minWidth: 76,
      minHeight: 32,
      margin: (unit * 1) / 2,
    },
  });

const messages = defineMessages({
  badgePCP: {
    id: 'components.v2.CardFacilityAvailabilities.badgePCP',
    defaultMessage: 'Your PCP',
  },
  showOnTimeZoneOption: {
    id: 'components.v2.CardFacilityAvailabilities.showOnTimeZoneOption',
    defaultMessage: 'All times are based on {timeZoneName} ({timeZoneAbbreviation})',
  },
});

export interface CardFacilityAvailabilitiesProps
  extends WithStyles<typeof styles>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  key: number | string;
  groupByFacility: {
    facility: TFacility;
    group: Array<{
      provider: TProvider;
      availabilities: TAvailability[];
    }>;
  };
  primaryCareProvider?: EntityID;
  onAvailabilityChange: (availability: TAvailability) => void;
}

const CardFacilityAvailabilities: React.FC<CardFacilityAvailabilitiesProps> = ({
  classes,
  key,
  groupByFacility,
  primaryCareProvider,
  onAvailabilityChange,
}) => {
  const { formatMessage } = useIntl();
  const currentMaster = useSelector(selectCurrentMaster); // Master Account
  const currentUser = useSelector(selectCurrentUser); // Widget User
  // Try getting the settings from master or widget user.
  const shouldHideProviderDetails = useMemo(
    () =>
      get(
        currentMaster,
        'settings.scheduler.hideProviderDetails',
        get(currentUser, 'settings.scheduler.hideProviderDetails', false),
      ),
    [currentMaster, currentUser],
  );

  const [availabilityIdSelected, setAvailabilityIdSelected] = useState('');

  return (
    <Paper key={key} elevation={1} square>
      <ListItem>
        <ListItemIcon>
          <SvgIconWrapper Icon={DomainIcon} color="textPrimary" fontSize="small" />
        </ListItemIcon>
        <ListItemText
          className={classes.facilityText}
          primary={<Typography variant="h6">{groupByFacility.facility.name}</Typography>}
          secondary={<Typography variant="caption">{AddressService.toString(groupByFacility.facility)}</Typography>}
        />
      </ListItem>
      <div className={classes.providers}>
        {map(groupByFacility.group, (groupByProvider, index) => {
          const { provider } = groupByProvider;
          const isPrimary = primaryCareProvider === provider._id;

          return (
            <div key={`${index}-${provider._id}`} className={classes.provider}>
              <UserAvatar
                className={classes.providerAvatar}
                user={shouldHideProviderDetails ? false : (provider as any)}
              />
              <div className={classes.providerName}>
                <Typography className={classes.providerText} variant="body1">
                  {shouldHideProviderDetails ? `Provider ${index + 1}` : provider.name}
                  {isPrimary && (
                    <Button
                      className={classes.buttonPCP}
                      icon={<Star fontSize="small" />}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      {formatMessage(messages.badgePCP)}
                    </Button>
                  )}
                </Typography>
              </div>
              <div className={classes.availabilities}>
                {map(groupByProvider.availabilities, (availability) => (
                  <Button
                    key={availability._id}
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={availabilityIdSelected === availability._id}
                    onClick={() => {
                      setAvailabilityIdSelected(availability._id);
                      onAvailabilityChange(availability);
                    }}
                  >
                    {availabilityIdSelected === availability._id ? (
                      <CircularProgress size={16} />
                    ) : (
                      moment(availability.date)
                        .format('LT')
                        .toLowerCase()
                        .replace(' ', '')
                    )}
                  </Button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Paper>
  );
};

export default withStyles(styles, { name: 'CardFacilityAvailabilities' })(CardFacilityAvailabilities);
